import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /auth/callback — the redirect-URI landing page registered with Entra.
 *
 * MSAL processes the OAuth response inside <MsalProvider>'s initialisation
 * (`handleRedirectPromise` is called from initMsal() in main.tsx). By the
 * time this component mounts, the active account is already set in MSAL's
 * cache — we just need to wait one render for the AppShell's /me query to
 * fire, then redirect to the user's persona-appropriate landing page.
 *
 * Why the indirection: MSAL's `navigateToLoginRequestUrl=true` would route
 * us straight back to whichever route the user clicked Sign In from; but
 * we route through this page so we can show a clean "Signing you in…"
 * state and centralise post-login navigation.
 */
import { useMsal } from '@azure/msal-react';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { defaultLandingFor, resolvePersona, useAuthStore } from '@/store/auth';
export function AuthCallbackPage() {
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const me = useAuthStore((s) => s.me);
    useEffect(() => {
        // Two-stage forward:
        //   1. If `me` is already known (rare: a prior tab populated the store),
        //      route straight to the persona's landing page.
        //   2. Otherwise, as soon as MSAL has an active account, forward to `/`.
        //      AppShell mounts there, fires /me, and dispatches onward.
        //
        // The previous implementation only watched `me`, but `me` is set by
        // AppShell — and AppShell doesn't mount on /auth/callback. Result: the
        // page hung on the spinner forever. The MSAL-account branch breaks
        // the deadlock.
        if (me) {
            const persona = resolvePersona(me);
            navigate(defaultLandingFor(persona), { replace: true });
            return;
        }
        if (accounts.length > 0) {
            navigate('/', { replace: true });
        }
    }, [me, accounts.length, navigate]);
    // Edge case: somebody hit /auth/callback directly without going through
    // the Microsoft redirect — kick them to /login.
    if (accounts.length === 0 && !me) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950", children: _jsxs("div", { className: "flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300", children: [_jsx(Loader2, { className: "h-5 w-5 animate-spin", "aria-hidden": "true" }), "Signing you in\u2026"] }) }));
}
